import type { EarnQuestId, EarnResponse } from '@xchange/shared';
import type { PairView } from './api.js';
import { amountText, xgeText, type Asset } from './assets.js';

/**
 * The Earn screen's words and sums, pure so they can be tested. One sentence
 * a line. XGE is paid for bids on the book and for swaps, never for opening
 * the app: nothing here says claim, mine or earn, and nothing names a price,
 * a profit, a return or an investment. A bid is an order to buy, an offer an
 * order to sell.
 */

/** Each step of the ladder, as one short line. */
export const QUEST_TEXT: Record<EarnQuestId, string> = {
  wallet: 'Make your wallet',
  words: 'Type your twelve words back',
  funds: 'Add funds',
  swap: 'Swap $10 or more with someone new',
  bid: 'Have a bid counted within 10% of the best offer',
  day: 'Keep a bid within 10% for a whole UTC day',
};

type Quest = EarnResponse['quests'][number];
type JobRow = EarnResponse['job']['pairs'][number];

const NTH = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh'];

/**
 * What a step's second line says once it is done; empty before. A step done
 * before the account has its counted days says when its XGE comes, one line.
 */
export function questStatus(q: Quest, minCountedDays = 3): string {
  if (!q.done || q.xge === '0') return '';
  if (q.paid === 'days')
    return `Arrives after your ${NTH[minCountedDays - 1] ?? ordinal(minCountedDays)} counted day.`;
  if (q.paid === 'paid') return 'Paid.';
  if (q.paid === 'accrued') return 'Arrives after midnight UTC.';
  return 'Counted. Paid within a day.';
}

/**
 * The quiz's XGE line, one sentence. `each` and `waiting` are XGE minor units.
 * Nothing here says earn, win, prize or free, and no amount is in dollars.
 */
export function quizXgeLine(q: EarnResponse['quiz']): string {
  if (!q.on) return '';
  const each = wholeXge(q.xgeEach);
  const waiting = wholeXge(q.waitingXge);
  switch (q.paid) {
    case 'paid':
      return 'XGE for your right answers: arrived.';
    case 'accrued':
      return 'XGE for your right answers: after midnight UTC.';
    case 'waiting':
      return `${waiting} XGE for your right answers, after midnight UTC.`;
    case 'swap':
      return `${waiting} XGE for your right answers, after your first $10 swap.`;
    default:
      return q.proven
        ? `${each} XGE a right answer, after midnight UTC.`
        : `${each} XGE a right answer, after your first $10 swap.`;
  }
}

/** "150", not "150.00": the ladder's amounts are whole. */
export function wholeXge(units: string | bigint): string {
  return xgeText(units).replace(/\.00$/, '');
}

/** "1st", "2nd", "3rd", "11th", "22nd". */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const ones = n % 10;
  return `${n}${ones === 1 ? 'st' : ones === 2 ? 'nd' : ones === 3 ? 'rd' : 'th'}`;
}

/** The one line under a bid on the daily job. */
export function jobLine(row: JobRow, bandPercent: number, minUsd: string): string {
  if (row.bid === null) return '';
  if (row.distancePercent === null) return 'No offer of $10 or more to measure it against yet.';
  // The band is measured against the higher of the best offer and the week's trades.
  const against =
    row.bestOffer !== null && row.bestOffer.price === row.reference
      ? 'the best offer'
      : "this week's trades";
  const away = `${row.distancePercent.toFixed(1)}% under ${against}`;
  if (BigInt(row.bid.usdc) < BigInt(minUsd))
    return `${away}, and under ${usdText(minUsd).replace(/\.00$/, '')}, so it does not count.`;
  if (row.inside) return `${away}: inside the band.`;
  return `${away}: outside the ${bandPercent}% band.`;
}

/** The pair a new bid is sent to: the first USDC pair with an offer to bid near. */
export function pairToBid(e: EarnResponse): JobRow | null {
  return e.job.pairs.find((p) => p.from !== null) ?? null;
}

const DECIMALS: Record<Asset, bigint> = { GAJU: 18n, USDC: 6n, XGE: 6n };

/**
 * The bid the button fills into Advanced: the least price inside the band,
 * and enough of the coin for at least $10, or for the dollars the old bid
 * held if more, rounded up to whole coins so it never falls short.
 */
export function bidPreset(
  pair: Pick<PairView, 'name' | 'base' | 'priceUnit'>,
  row: JobRow,
  minUsd: string,
): { pair: string; price: bigint; amount: bigint } | null {
  if (row.from === null) return null;
  const price = BigInt(row.from);
  if (price <= 0n) return null;
  const usd =
    row.bid !== null && BigInt(row.bid.usdc) > BigInt(minUsd)
      ? BigInt(row.bid.usdc)
      : BigInt(minUsd);
  const units = (usd * BigInt(pair.priceUnit) + price - 1n) / price;
  const coin = 10n ** DECIMALS[pair.base as Asset];
  const whole = ((units + coin - 1n) / coin) * coin;
  return { pair: pair.name, price, amount: whole };
}

/** "$10.80" for a bid's dollars. */
export function usdText(units: string): string {
  return amountText('USDC', BigInt(units));
}
